import Button from "~/components/Button/Button";
import EditIcon from "~/icons/EditIcon";
import MinusIcon from "~/icons/MinusIcon";
import PlusIcon from "~/icons/PlusIcon";
import {
  getFinalPrice,
  getModifiersList,
  getTotalPrice,
} from "~/helpers/cartHelpers";

import React, { useContext, useMemo } from "react";
import ThemeContext from "~/contexts/ThemeContext";
import {
  CartItemModifiersFontSize,
  CartItemNameFontSize,
  CartItemPriceFontSize,
} from "~/theme/models/themeModels";
import TaxContext from "~/contexts/TaxContext";

import styles from "./CartItem.module.css";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import RemoveIcon from "~/icons/RemoveIcon";
import { CartLine, CartLineItem, OrderSource } from "~/entities/orderEntities";
import { lineOfferSavings } from "~/helpers/offerAmounts";
import { offerSavingBackground, offerSavingColour } from "~/helpers/offerTheme";
import OrderContext from "~/contexts/OrderContext";

// Discounts on modifiers, e.g. "Extra bacon" with "Free bacon Mondays"
const modifierSavings = (item: CartLineItem) => {
  const res: { name: string; discountName: string; amount: number | null }[] =
    [];
  const walk = (children: CartLineItem[]) => {
    for (const child of children) {
      for (const discount of child.discounts) {
        res.push({
          name: child.product.name,
          discountName: discount.name,
          amount: discount.amount ?? null,
        });
      }
      walk(child.children);
    }
  };
  walk(item.children);
  return res;
};

type OrderCartItemProps = {
  line: CartLine;
  updateLine: (line: CartLine) => void;
  openEditLineModal: VoidFunction;
  orderSource: OrderSource;
  // e.g. "Burger Combo: add a drink" when the line is part of a deal that's one item short
  dealHint?: string;
};

const CartLineView: React.FC<OrderCartItemProps> = ({
  line,
  updateLine,
  openEditLineModal,
  dealHint,
}) => {
  const { template } = useContext(ThemeContext);
  const { formatAmount } = useContext(TaxContext);
  const { readonlyCart } = useContext(OrderContext);

  const product = line.item.product;

  const modifiers = useMemo(() => getModifiersList(line.item), [line]);

  // Styles
  const backgroundColor = template.cart.backgroundColour;
  const fontColour = template.cart.fontColour;
  const imageBackgroundColour = template.cart.item.imageBackgroundColour;
  const cartItemNameFontSize =
    CartItemNameFontSize[template.cart.item.nameFontSize];
  const cartItemNameFontColor = template.cart.item.nameFontColour;
  const cartItemModifiersFontSize =
    CartItemModifiersFontSize[template.cart.item.modifiersFontSize];
  const cartItemModifiersFontColor = template.cart.item.modifiersFontColour;
  const cartItemPriceFontSize =
    CartItemPriceFontSize[template.cart.item.priceFontSize];
  const cartItemPriceFontColor = template.cart.item.priceFontColour;

  const totalPrice = getTotalPrice(line.item);
  const finalPrice = getFinalPrice(line.item);

  // Pills show what each offer actually saved on this line (qty included), and modifier discounts get their own
  const savingPills = [
    ...lineOfferSavings(line).map((x) => ({
      label: x.name,
      amount: x.amount,
    })),
    ...modifierSavings(line.item).map((x) => ({
      label: `${x.discountName} on ${x.name}`,
      amount: x.amount,
    })),
  ];
  const savingColour = offerSavingColour(template);
  const savingBackground = offerSavingBackground(template);

  return (
    <div
      className={styles.wrapper}
      style={{ backgroundColor, color: fontColour }}
    >
      <div
        className={styles.imageWrapper}
        style={{ backgroundColor: imageBackgroundColour }}
      >
        {product.image && (
          <img
            className={styles.image}
            src={uploadcareConfig.getUrl(
              product.image.fileId,
              product.image.modifiers,
            )}
            alt={product.image.fileName}
          />
        )}
      </div>
      <div className={styles.productDetails}>
        <span
          className={styles.productName}
          style={{
            fontSize: cartItemNameFontSize,
            color: cartItemNameFontColor,
          }}
        >
          {product.name}
        </span>
        <span
          className={styles.productModifiers}
          style={{
            fontSize: cartItemModifiersFontSize,
            color: cartItemModifiersFontColor,
          }}
        >
          {modifiers}
        </span>
        <span
          className={styles.editButton}
          style={{ color: `${fontColour}99` }}
          onClick={() => openEditLineModal()}
        >
          <EditIcon color={`${fontColour}99`} />
          <span>Edit</span>
        </span>
      </div>
      <div className={styles.actions}>
        <div className={styles.counterWrapper}>
          <Button
            variant="counter-secondary"
            disabled={readonlyCart}
            onClick={() =>
              updateLine({
                ...line,
                item: {
                  ...line.item,
                  qty: Math.max(line.item.qty - 1, 0),
                },
              })
            }
          >
            {line.item.qty === 1 ? <RemoveIcon size={16} /> : <MinusIcon />}
          </Button>
          <div className={styles.count}>{line.item.qty}</div>
          <Button
            variant="counter-primary"
            onClick={() =>
              updateLine({
                ...line,
                item: { ...line.item, qty: line.item.qty + 1 },
              })
            }
            disabled={line.item.product.soldOut || readonlyCart}
          >
            <PlusIcon />
          </Button>
        </div>
        <div className={styles.priceWrapper}>
          {totalPrice !== finalPrice && (
            <span
              className={styles.productPrice}
              style={{
                fontSize: cartItemPriceFontSize,
                color: cartItemPriceFontColor,
                textDecoration: "line-through",
                marginTop: "auto",
              }}
            >
              {formatAmount(totalPrice)}
            </span>
          )}
          <span
            className={styles.productFinalPrice}
            style={{
              fontSize: cartItemPriceFontSize,
              color: cartItemPriceFontColor,
            }}
          >
            {formatAmount(finalPrice)}
          </span>
        </div>
      </div>
      <div className={styles.discounts}>
        {savingPills.map((pill, i) => (
          <div
            key={`${pill.label}-${i}`}
            className={styles.discountTag}
            style={{ backgroundColor: savingBackground, color: savingColour }}
          >
            {pill.label}
            {pill.amount != null &&
              pill.amount > 0 &&
              ` \u2212${formatAmount(pill.amount)}`}
          </div>
        ))}
        {dealHint && (
          <div
            className={styles.dealHint}
            style={{
              borderColor: template.global.button.primaryButtonColour,
              color: template.global.button.primaryButtonColour,
            }}
          >
            {dealHint}
          </div>
        )}
      </div>
    </div>
  );
};

export default CartLineView;
